import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import About2 from "../assets/About2.jpg";
import AboutImg from "../assets/About.png";
import { getAboutUs } from "../services/api/getAll";
import { useTranslation } from "react-i18next";
import bgImage from "../assets/kiki2.jpeg";

const AboutUsMobile = () => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { t, i18n } = useTranslation();

  const [aboutContent, setAboutContent] = useState("");

  const renderHighlightedText = (text: string) => {
    if (!text) return "";
    const parts = text.split(/(TowHelp)/gi);
    return parts.map((part, index) =>
      part.toLowerCase() === "towhelp" ? (
        <span
          key={index}
          style={{
            color: "#FFAE00",
            fontWeight: 800,
            display: "inline-block",
            direction: "ltr",
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAboutUs();
        const item = response.items?.[response?.items?.length - 1];
        const preferredLanguage = localStorage.getItem("language") || "en";

        const translation = item?.translations.find(
          (t: { languageCode: string }) => t.languageCode === preferredLanguage
        );
        setAboutContent(translation?.content || "");
      } catch (err: any) {
        console.error("Error fetching about us data:", err);
      }
    };

    fetchData();
  }, []);

  if (!isMobile) return null;

  return (
    <Box
      sx={{
        py: 8,
        px: 2,
        background: "linear-gradient(180deg, #121212, #0a0a0a, #121212)",
        color: "#fff",
        position: "relative",
        overflow: "hidden",
      }}
    >

      
      <motion.div
        key={`${aboutContent ? 1 : 0}-${i18n.language}`}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        style={{ zIndex: 2, position: "relative" }}
      >
        <Box
          textAlign="center"
          mb={4}
          sx={{
            direction: i18n.language.startsWith("ar") ? "rtl" : "ltr"
          }}
        >
          <Typography
            component="span"
            variant="h3"
            fontWeight={900}
            sx={{
              fontFamily: "Poppins, Ozen",
              color: "#FFAE00",
              textTransform: "uppercase",
              fontSize: "2rem",
            }}
          >
            {t("About")}{" "}
          </Typography>
          <Typography
            component="span"
            variant="h3"
            fontWeight={900}
            sx={{
              fontFamily: "Ozen",
              color: "#FFAE00",
              textTransform: "uppercase",
              fontSize: "2rem",
            }}
          >
            TowHelp
          </Typography>
        </Box>

        <Typography
          variant="body1"
          className="glass-panel"
          sx={{
            fontFamily: "Poppins",
            color: "#e0e0e0",
            fontSize: "0.95rem",
            lineHeight: 1.7,
            p: 3,
            borderRadius: "20px",
            border: "1px solid rgba(255, 174, 0, 0.15) !important",
            boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.4)",
            maxWidth: "100%",
            textAlign: i18n.language.startsWith("ar") ? "right" : "left",
            direction: i18n.language.startsWith("ar") ? "rtl" : "ltr",
          }}
        >
          {renderHighlightedText(aboutContent)}
        </Typography>
      </motion.div>

      {/* Redesigned Responsive Mockups Grid (UX Optimized) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: { xs: 2, sm: 4 },
          mt: 5,
          position: "relative",
          zIndex: 2,
          px: 1,
        }}
      >
        {/* Left Mockup Card */}
        <motion.div
          initial={{ opacity: 0, x: -40, rotate: -8 }}
          whileInView={{ opacity: 1, x: 0, rotate: -5 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{ width: "46%", maxWidth: "200px" }}
        >
          <motion.div
            animate={{
              y: [0, -8, 0],
            }}
            transition={{
              duration: 4.5,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
          >
            <Box
              sx={{
                width: "100%",
                aspectRatio: "9/19",
                borderRadius: "28px",
                overflow: "hidden",
                border: "6px solid #1a1a1a",
                boxShadow: "0 12px 36px rgba(0,0,0,0.6)",
                background: "#000",
                position: "relative",
                transition: "all 0.3s ease",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "0",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "70px",
                  height: "12px",
                  backgroundColor: "#1a1a1a",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  zIndex: 10,
                },
                "&:hover": {
                  transform: "rotate(0deg) scale(1.05) !important",
                  boxShadow: "0 20px 48px rgba(255, 174, 0, 0.15)",
                }
              }}
            >
              <Box
                component="img"
                src={About2}
                alt="TowHelp Mobile Screen 1"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          </motion.div>
        </motion.div>

        {/* Right Mockup Card */}
        <motion.div
          initial={{ opacity: 0, x: 40, rotate: 8 }}
          whileInView={{ opacity: 1, x: 0, rotate: 5 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
          style={{ width: "46%", maxWidth: "200px" }}
        >
          <motion.div
            animate={{
              y: [0, -8, 0],
            }}
            transition={{
              duration: 5.5,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
              delay: 0.4,
            }}
          >
            <Box
              sx={{
                width: "100%",
                aspectRatio: "9/19",
                borderRadius: "28px",
                overflow: "hidden",
                border: "6px solid #1a1a1a",
                boxShadow: "0 12px 36px rgba(0,0,0,0.6)",
                background: "#000",
                position: "relative",
                transition: "all 0.3s ease",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "0",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "70px",
                  height: "12px",
                  backgroundColor: "#1a1a1a",
                  borderBottomLeftRadius: "8px",
                  borderBottomRightRadius: "8px",
                  zIndex: 10,
                },
                "&:hover": {
                  transform: "rotate(0deg) scale(1.05) !important",
                  boxShadow: "0 20px 48px rgba(255, 174, 0, 0.15)",
                }
              }}
            >
              <Box
                component="img"
                src={AboutImg}
                alt="TowHelp Mobile Screen 2"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Box>
          </motion.div>
        </motion.div>
      </Box>
    </Box>
  );
};

export default AboutUsMobile;

